import React, { useState, useRef, useEffect } from "react";
import Layout from "../../Component/Layout";
import DatePicker from "react-datepicker";
import axios from "axios";
import { PURCHASE_API } from "../../apiConfig";
import "react-datepicker/dist/react-datepicker.css";
import { useLocation, useNavigate } from "react-router-dom";
import {
    FaUsers,
    FaCalendarCheck,
    FaCalendarAlt,
    FaCalendarTimes,
    FaSyncAlt,
    FaDownload
} from "react-icons/fa";
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip,
} from "recharts";

// ==========================================
// STATIC / DUMMY DATA (wire to real API later)
// ==========================================
const kpis = {
    totalOutstanding: { value: "138.7", delta: "8.7%", vs: "vs Apr 30, 2025" },
    current: { value: "42.6", delta: "5.6%", vs: "vs Apr 30, 2025" },
    days30: { value: "28.1", delta: "4.3%", vs: "vs Apr 30, 2025" },
    days60: { value: "23.4", delta: "6.1%", vs: "vs Apr 30, 2025" },
    days90Plus: { value: "44.6", delta: "12.8%", vs: "vs Apr 30, 2025", negative: true },
};


// Turns { value, unit } (from the API) into "₹ 1,208" / "₹ 1.2 Lakh" / "₹ 0.5 Cr" text.
function formatAmountDisplay(formatted) {
    if (!formatted) return "₹ 0";
    const { value, unit } = formatted;
    return unit ? `₹ ${value} ${unit}` : `₹ ${value}`;
}

// Turns an ISO date string from the API into "12 Apr 2025" style text.
function formatInvoiceDate(dateStr) {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d)) return "—";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

// ==========================================
// SHARED STYLE HELPERS (matches PayablesDashboard.js / CFODashboard.js)
// ==========================================
const card = {
    background: "#fff",
    borderRadius: "14px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 4px 6px -1px rgba(0,0,0,.05),0 2px 4px -1px rgba(0,0,0,.03)",
    padding: "20px 22px",
};

const cardHeader = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "14px",
};

const iconBadge = (bg, color) => ({
    width: "34px",
    height: "34px",
    borderRadius: "10px",
    background: bg,
    color: color,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
});

const viewAllLink = {
    fontSize: "13px",
    fontWeight: 700,
    color: "#3b82f6",
    cursor: "pointer",
};

const table = { width: "100%", borderCollapse: "collapse", fontSize: "13px" };
const th = { textAlign: "left", padding: "8px 6px", color: "#94a3b8", fontWeight: 600, fontSize: "12px", borderBottom: "1px solid #f1f5f9" };
const td = { padding: "10px 6px", color: "#334155", borderBottom: "1px solid #f1f5f9" };

const bucketBadge = (bucket) => {
    const map = {
        "30 Days": { bg: "#fef3c7", color: "#d97706" },
        "60 Days": { bg: "#fce7f3", color: "#db2777" },
        "90+ Days": { bg: "#ede9fe", color: "#7c3aed" },
    };
    const c = map[bucket] || { bg: "#e2e8f0", color: "#334155" };
    return {
        background: c.bg,
        color: c.color,
        fontSize: "11.5px",
        fontWeight: 700,
        padding: "4px 10px",
        borderRadius: "999px",
        display: "inline-block",
    };
};

// ==========================================
// SMALL COMPONENTS
// ==========================================
function DonutChart({ data, centerValue, centerLabel, size = 220 }) {
    return (
        <div style={{ position: "relative", width: `${size}px`, height: `${size}px`, flexShrink: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie data={data} dataKey="value" nameKey="name" innerRadius={size * 0.32} outerRadius={size * 0.48} paddingAngle={2} stroke="none">
                        {data.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                        ))}
                    </Pie>
                    <Tooltip formatter={(val) => `₹${val} Cr`} />
                </PieChart>
            </ResponsiveContainer>
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                <div style={{ fontSize: `${Math.max(20, size * 0.1)}px`, fontWeight: 800, color: "#0f172a" }}>{centerValue}</div>
                <div style={{ fontSize: "12px", color: "#64748b" }}>{centerLabel}</div>
            </div>
        </div>
    );
}

function getCurrentMonthRange() {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { start, end };
}

function formatRangeLabel(start, end) {
    if (!start || !end) return "Select date range";
    const startStr = start.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const endStr = end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return `${startStr} – ${endStr}`;
}

function AccountsReceivableDashboard() {
    const navigate = useNavigate();
    const location = useLocation();
    const [refreshing, setRefreshing] = useState(false);
    const { start: defaultStart, end: defaultEnd } = getCurrentMonthRange();
    const [startDate, setStartDate] = useState(defaultStart);
    const [endDate, setEndDate] = useState(defaultEnd);
    const [showPicker, setShowPicker] = useState(false);
    const pickerRef = useRef(null);

    // ---- Total Outstanding (live, from /receivables-dashboard/total-outstanding) ----
    const [totalOutstanding, setTotalOutstanding] = useState(null);
    const [totalOutstandingLoading, setTotalOutstandingLoading] = useState(true);
    const [totalOutstandingError, setTotalOutstandingError] = useState(null);

    const fetchTotalOutstanding = async () => {
        setTotalOutstandingLoading(true);
        setTotalOutstandingError(null);
        try {
            const { data } = await axios.get(`${PURCHASE_API}/receivables-dashboard/total-outstanding`);
            if (!data.success) throw new Error(data.message || "Failed to load Total Outstanding");
            setTotalOutstanding(data.totalOutstanding);
        } catch (err) {
            console.error("Total Outstanding fetch error:", err);
            setTotalOutstandingError(err.response?.data?.message || err.message || "Failed to load data");
        } finally {
            setTotalOutstandingLoading(false);
        }
    };

    useEffect(() => {
        fetchTotalOutstanding();
    }, []);

    // Pulls one ageing bucket (Current / 30 Days / 60 Days / 90+ Days) out of the API response
    const getBucket = (name) => totalOutstanding?.buckets?.find((b) => b.name === name);

    // ---- Top 10 Overdue Customers (live, filtered by the selected date range) ----
    const [topCustomers, setTopCustomers] = useState(null);
    const [topCustomersLoading, setTopCustomersLoading] = useState(true);
    const [topCustomersError, setTopCustomersError] = useState(null);

    const toApiDateString = (date) => {
        if (!date) return "";
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    };

    const fetchTopCustomers = async () => {
        if (!startDate || !endDate) return;
        setTopCustomersLoading(true);
        setTopCustomersError(null);
        try {
            const { data } = await axios.get(`${PURCHASE_API}/receivables-dashboard/top-overdue-customers`, {
                params: { startDate: toApiDateString(startDate), endDate: toApiDateString(endDate) },
            });
            if (!data.success) throw new Error(data.message || "Failed to load Top Overdue Customers");
            setTopCustomers(data);
        } catch (err) {
            console.error("Top Overdue Customers fetch error:", err);
            setTopCustomersError(err.response?.data?.message || err.message || "Failed to load data");
        } finally {
            setTopCustomersLoading(false);
        }
    };

    useEffect(() => {
        fetchTopCustomers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    // ---- Overdue Invoice Details (live, filtered by the selected date range) ----
    const [overdueInvoicesLive, setOverdueInvoicesLive] = useState(null);
    const [overdueInvoicesLoading, setOverdueInvoicesLoading] = useState(true);
    const [overdueInvoicesError, setOverdueInvoicesError] = useState(null);

    const fetchOverdueInvoices = async () => {
        if (!startDate || !endDate) return;
        setOverdueInvoicesLoading(true);
        setOverdueInvoicesError(null);
        try {
            const { data } = await axios.get(`${PURCHASE_API}/receivables-dashboard/overdue-invoices`, {
                params: { startDate: toApiDateString(startDate), endDate: toApiDateString(endDate) },
            });
            if (!data.success) throw new Error(data.message || "Failed to load Overdue Invoice Details");
            setOverdueInvoicesLive(data);
        } catch (err) {
            console.error("Overdue Invoice Details fetch error:", err);
            setOverdueInvoicesError(err.response?.data?.message || err.message || "Failed to load data");
        } finally {
            setOverdueInvoicesLoading(false);
        }
    };

    useEffect(() => {
        fetchOverdueInvoices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                setShowPicker(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
        if (start && end) setShowPicker(false);
    };

    const handleRefresh = () => {
        setRefreshing(true);
        fetchTotalOutstanding();
        fetchTopCustomers();
        setTimeout(() => setRefreshing(false), 600);
    };

    // const totalAging = receivablesAging.reduce((s, d) => s + d.value, 0).toFixed(1);

    return (
        <Layout defaultSidebarOpen={false}>
            <div
                style={{
                    padding: "24px 32px",
                    backgroundColor: "#f8fafc",
                    minHeight: "calc(100vh - 60px)",
                }}
            >
                {/* ================= HEADER ================= */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: "16px",
                        marginBottom: "28px",
                    }}
                >
                    <div>
                        <h2 style={{ margin: "0 0 6px 0", color: "#0f172a", fontWeight: 700, fontSize: "26px" }}>
                         Accounts Receivables Dashboard
                        </h2>
                        <p style={{ color: "#64748b", margin: 0, fontSize: "14px" }}>
                            Track customer receivables and ageing overview
                        </p>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div style={{ position: "relative" }} ref={pickerRef}>
                            <div
                                onClick={() => setShowPicker((prev) => !prev)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    background: "#fff",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: "10px",
                                    padding: "9px 14px",
                                    fontSize: "13px",
                                    color: "#334155",
                                    fontWeight: 500,
                                    cursor: "pointer",
                                }}
                            >
                                <FaCalendarAlt size={13} color="#64748b" />
                                {formatRangeLabel(startDate, endDate)}
                            </div>

                            {showPicker && (
                                <div
                                    style={{
                                        position: "absolute",
                                        top: "calc(100% + 8px)",
                                        right: 0,
                                        zIndex: 100,
                                        background: "#fff",
                                        borderRadius: "10px",
                                        boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                                        border: "1px solid #e2e8f0",
                                        padding: "10px",
                                    }}
                                >
                                    <DatePicker
                                        selectsRange
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChange={handleDateChange}
                                        inline
                                        monthsShown={2}
                                    />
                                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px" }}>
                                        <button
                                            onClick={() => {
                                                const { start, end } = getCurrentMonthRange();
                                                setStartDate(start);
                                                setEndDate(end);
                                                setShowPicker(false);
                                            }}
                                            style={{
                                                background: "transparent",
                                                border: "1px solid #e2e8f0",
                                                borderRadius: "8px",
                                                padding: "6px 12px",
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                color: "#334155",
                                                cursor: "pointer",
                                            }}
                                        >
                                            This Month
                                        </button>
                                        <button
                                            onClick={() => setShowPicker(false)}
                                            style={{
                                                background: "#2563eb",
                                                border: "none",
                                                borderRadius: "8px",
                                                padding: "6px 14px",
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                color: "#fff",
                                                cursor: "pointer",
                                            }}
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                background: "#fff",
                                border: "1px solid #e2e8f0",
                                borderRadius: "10px",
                                padding: "9px 14px",
                                fontSize: "13px",
                                color: "#334155",
                                fontWeight: 600,
                                cursor: "pointer",
                            }}
                        >
                            All Plants <FaChevronDown size={10} />
                        </div> */}

                        <button
                            onClick={handleRefresh}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                background: "#2563eb",
                                border: "none",
                                borderRadius: "10px",
                                padding: "10px 16px",
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "#fff",
                                cursor: "pointer",
                            }}
                        >
                            <FaSyncAlt size={13} style={{ animation: refreshing ? "spin 0.8s linear infinite" : "none" }} />
                            Refresh
                        </button>

                        <div
                            style={{
                                width: "38px",
                                height: "38px",
                                borderRadius: "10px",
                                background: "#fff",
                                border: "1px solid #e2e8f0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#334155",
                                cursor: "pointer",
                            }}
                        >
                            <FaDownload size={14} />
                        </div>
                    </div>
                </div>

                {/* ================= KPI CARDS ================= */}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                        gap: "20px",
                        marginBottom: "24px",
                    }}
                >
                    <div style={{ ...card, cursor: "pointer" }} onClick={() => navigate("/finance/account-receivable/payment-run")}>
                        <div style={cardHeader}>
                            <div style={iconBadge("#eff6ff", "#2563eb")}>
                                <FaUsers size={16} />
                            </div>
                            <span style={{ fontSize: "13px", fontWeight: 700, color: "#334155" }}>Total Outstanding <span style={{ color: "#94a3b8", fontWeight: 500 }}>(₹ Cr)</span></span>
                        </div>
                        <div style={{ fontSize: "26px", fontWeight: 800, color: "#0f172a" }}>
                            {totalOutstandingLoading
                                ? "Loading…"
                                : totalOutstandingError
                                    ? "—"
                                    : formatAmountDisplay(totalOutstanding?.formatted)}
                        </div>
                    </div>

                    <div style={{ ...card, cursor: "pointer" }} onClick={() => navigate("/finance/account-receivable/payment-run")}>
                        <div style={cardHeader}>
                            <div style={iconBadge("#f0fdf4", "#16a34a")}>
                                <FaCalendarCheck size={16} />
                            </div>
                            <span style={{ fontSize: "13px", fontWeight: 700, color: "#334155" }}>Current <span style={{ color: "#94a3b8", fontWeight: 500 }}>(₹ Cr)</span></span>
                        </div>
                        <div style={{ fontSize: "26px", fontWeight: 800, color: "#0f172a" }}>
                            {totalOutstandingLoading ? "…" : formatAmountDisplay(getBucket("Current")?.formatted)}
                        </div>
                    </div>

                    <div style={{ ...card, cursor: "pointer" }} onClick={() => navigate("/finance/account-receivable/payment-run")}>
                        <div style={cardHeader}>
                            <div style={iconBadge("#fff7ed", "#f97316")}>
                                <FaCalendarAlt size={16} />
                            </div>
                            <span style={{ fontSize: "13px", fontWeight: 700, color: "#334155" }}>30 Days <span style={{ color: "#94a3b8", fontWeight: 500 }}>(₹ Cr)</span></span>
                        </div>
                        <div style={{ fontSize: "26px", fontWeight: 800, color: "#0f172a" }}>
                            {totalOutstandingLoading ? "…" : formatAmountDisplay(getBucket("30 Days")?.formatted)}
                        </div>
                    </div>

                    <div style={{ ...card, cursor: "pointer" }} onClick={() => navigate("/finance/account-receivable/payment-run")}>
                        <div style={cardHeader}>
                            <div style={iconBadge("#fdf2f8", "#db2777")}>
                                <FaCalendarAlt size={16} />
                            </div>
                            <span style={{ fontSize: "13px", fontWeight: 700, color: "#334155" }}>60 Days <span style={{ color: "#94a3b8", fontWeight: 500 }}>(₹ Cr)</span></span>
                        </div>
                        <div style={{ fontSize: "26px", fontWeight: 800, color: "#0f172a" }}>
                            {totalOutstandingLoading ? "…" : formatAmountDisplay(getBucket("60 Days")?.formatted)}
                        </div>
                    </div>

                    <div style={{ ...card, cursor: "pointer" }} onClick={() => navigate("/finance/account-receivable/payment-run")}>
                        <div style={cardHeader}>
                            <div style={iconBadge("#f5f3ff", "#7c3aed")}>
                                <FaCalendarTimes size={16} />
                            </div>
                            <span style={{ fontSize: "13px", fontWeight: 700, color: "#334155" }}>90+ Days <span style={{ color: "#94a3b8", fontWeight: 500 }}>(₹ Cr)</span></span>
                        </div>
                      <div style={{ fontSize: "26px", fontWeight: 800, color: "#0f172a" }}>
                            {totalOutstandingLoading ? "…" : formatAmountDisplay(getBucket("90+ Days")?.formatted)}
                        </div>
                    </div>
                </div>

                {/* ================= AGEING + TOP CUSTOMERS ROW ================= */}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1.3fr",
                        gap: "20px",
                        marginBottom: "20px",
                    }}
                >
                    {/* Receivables Ageing Summary */}
                    <div style={{ ...card, display: "flex", flexDirection: "column" }}>
                        <div style={{ fontSize: "14px", fontWeight: 700, color: "#334155", marginBottom: "14px" }}>
                            Receivables Ageing Summary <span style={{ color: "#94a3b8", fontWeight: 500 }}>(₹ Cr)</span>
                        </div>
                       <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "24px", flexWrap: "wrap" }}>
                            <DonutChart
                                data={(totalOutstanding?.buckets || []).map((b) => ({ name: b.name, value: b.raw, color: b.color }))}
                                centerValue={totalOutstandingLoading ? "…" : formatAmountDisplay(totalOutstanding?.formatted)}
                                centerLabel="Total Outstanding"
                                size={280}
                            />
                            <div style={{ flex: 1, minWidth: "170px", display: "flex", flexDirection: "column", gap: "20px" }}>
                                {(totalOutstanding?.buckets || []).map((d) => (
                                    <div key={d.name} style={{ fontSize: "13.5px" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
                                            <span style={{ width: 10, height: 10, borderRadius: "50%", background: d.color, display: "inline-block" }} />
                                            <span style={{ color: "#334155", fontWeight: 600 }}>{d.name}</span>
                                        </div>
                                        <div style={{ marginLeft: "17px", color: "#0f172a", fontWeight: 700 }}>
                                            {formatAmountDisplay(d.formatted)} <span style={{ color: "#94a3b8", fontWeight: 500 }}>({d.pct}%)</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div style={{ fontSize: "12px", fontWeight: 600, color: "#16a34a", marginTop: "14px" }}>
                            ▲ 8.7% vs Apr 30, 2025
                        </div>
                    </div>

                    {/* Top 10 Overdue Customers */}
                    <div style={card}>
                        <div style={{ fontSize: "14px", fontWeight: 700, color: "#334155", marginBottom: "14px" }}>
                            Top 10 Overdue Customers <span style={{ color: "#94a3b8", fontWeight: 500 }}>(₹ Cr)</span>
                        </div>
                        <div style={{ overflowX: "auto" }}>
                            <table style={table}>
                                <thead>
                                    <tr>
                                        <th style={th}>Sl No</th>
                                        <th style={th}>Customer Name</th>
                                        <th style={{ ...th, textAlign: "right" }}>Total Outstanding</th>
                                        <th style={{ ...th, textAlign: "right" }}>Overdue (30+ Days)</th>
                                        <th style={{ ...th, textAlign: "right" }}>90+ Days</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {topCustomersLoading ? (
                                        <tr><td style={td} colSpan={5}>Loading…</td></tr>
                                    ) : topCustomersError ? (
                                        <tr><td style={{ ...td, color: "#ef4444" }} colSpan={5}>{topCustomersError}</td></tr>
                                    ) : (
                                        (topCustomers?.customers || []).map((c, i) => (
                                            <tr key={c.name}>
                                                <td style={td}>{i + 1}</td>
                                                <td style={{ ...td, fontWeight: 600, color: "#0f172a" }}>{c.name}</td>
                                                <td style={{ ...td, textAlign: "right" }}>{formatAmountDisplay(c.totalOutstandingFormatted)}</td>
                                                <td style={{ ...td, textAlign: "right" }}>{formatAmountDisplay(c.overdue30Formatted)}</td>
                                                <td style={{ ...td, textAlign: "right" }}>{formatAmountDisplay(c.overdue90Formatted)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                                {!topCustomersLoading && !topCustomersError && (
                                    <tfoot>
                                        <tr>
                                            <td style={{ ...td, fontWeight: 800, color: "#2563eb", borderBottom: "none" }} colSpan={2}>Total</td>
                                            <td style={{ ...td, textAlign: "right", fontWeight: 800, color: "#2563eb", borderBottom: "none" }}>{formatAmountDisplay(topCustomers?.totals?.totalOutstandingFormatted)}</td>
                                            <td style={{ ...td, textAlign: "right", fontWeight: 800, color: "#2563eb", borderBottom: "none" }}>{formatAmountDisplay(topCustomers?.totals?.overdue30Formatted)}</td>
                                            <td style={{ ...td, textAlign: "right", fontWeight: 800, color: "#2563eb", borderBottom: "none" }}>{formatAmountDisplay(topCustomers?.totals?.overdue90Formatted)}</td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    </div>
                </div>

                {/* ================= OVERDUE INVOICE DETAILS ================= */}
                <div style={card}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                        <span style={{ fontSize: "14px", fontWeight: 700, color: "#334155" }}>Overdue Billing Details</span>
                        <span style={viewAllLink}>View All</span>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                        <table style={table}>
                            <thead>
                                <tr>
                                    <th style={th}>Billing ID</th>
                                    <th style={th}>Customer Name</th>
                                    <th style={th}>Billing Date</th>
                                    <th style={th}>Due Date</th>
                                    <th style={{ ...th, textAlign: "right" }}>Final Price after GST (₹)</th>
                                    <th style={{ ...th, textAlign: "right" }}>Outstanding (₹)</th>
                                    <th style={{ ...th, textAlign: "right" }}>Days Overdue</th>
                                    <th style={th}>Ageing Bucket</th>
                                </tr>
                            </thead>
                            <tbody>
                                {overdueInvoicesLoading ? (
                                    <tr><td style={td} colSpan={8}>Loading…</td></tr>
                                ) : overdueInvoicesError ? (
                                    <tr><td style={{ ...td, color: "#ef4444" }} colSpan={8}>{overdueInvoicesError}</td></tr>
                                ) : (
                                    (overdueInvoicesLive?.invoices || []).map((r) => (
                                        <tr key={r.bi_id}>
                                            <td style={{ ...td, fontWeight: 700, color: "#0f172a" }}>
                                                <span
                                                    onClick={() =>
                                                        navigate("/cable/billing/billing", {
                                                            state: {
                                                                openBiId: r.bi_id,
                                                                returnTo: location.pathname
                                                            }
                                                        })
                                                    }
                                                    style={{ color: "#1976d2", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: "2px" }}
                                                >
                                                    {r.bi_id}
                                                </span>
                                            </td>
                                            <td style={td}>{r.customer}</td>
                                            <td style={td}>{formatInvoiceDate(r.bi_date)}</td>
                                            <td style={td}>{formatInvoiceDate(r.due_date)}</td>
                                            <td style={{ ...td, textAlign: "right" }}>{formatAmountDisplay(r.invoiceAmountFormatted)}</td>
                                            <td style={{ ...td, textAlign: "right" }}>{formatAmountDisplay(r.outstandingFormatted)}</td>
                                            <td style={{ ...td, textAlign: "right" }}>{r.daysOverdue}</td>
                                            <td style={td}>
                                                <span style={bucketBadge(r.ageingBucket)}>{r.ageingBucket}</span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div style={{ textAlign: "center", fontSize: "12px", color: "#94a3b8", marginTop: "24px" }}>
                    All receivable data is real-time and synchronized across all plants
                </div>

                <style>{`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        </Layout>
    );
}

export default AccountsReceivableDashboard;